import clsx from 'clsx';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { useNavigate } from 'react-router-dom';
import ParticipantList from './ParticipantList';
import TrainingContext from '../TrainingContext';
import AddParticipantForm from './AddParticipantForm';
import { getTrainingParticipants } from 'actions/trainings';
import Permission from 'components/Subscription/Permission';
import { useState, useEffect, useContext } from 'react';
import { TrainingParticipant } from 'types/api/TrainingParticipant';
import RequestPaymentModal from './RequestPaymentModal';
import CancelModal from './CancelModal';
import StatusBar from './StatusBar';
import useCurrentUser from 'hooks/useCurrentUser';
import queryString from 'query-string';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuArrow,
  Text
} from '@teachstonellc/component-library';
import {
  ChevronDown,
  PencilEdit,
  Download
} from '@teachstonellc/component-library/dist/icons';

import {
  mapParticipantsToOptions,
  paymentDisabled,
  cancelDisabled,
  reinviteDeclinedDisabled,
  addCanceledDisabled,
  editParticipantEmailDisabled,
  exportDisabled,
  exportUrl,
  withinSevenDays,
  getMaxParticipants,
  getTotalByStatus
} from './utils';
import InviteCanceledModal from './InviteCanceledModal';
import ReinviteDeclinedModal from './ReinviteDeclinedModal';
import EditEmailModal from './EditEmailModal';

function Participants() {
  const navigate = useNavigate();
  const { training } = useContext(TrainingContext);
  const trainingIsActive = training.status === 'active';
  const [values, setValues] = useState<number[]>([]);
  const [participants, setParticipants] = useState<TrainingParticipant[]>([]);
  const [canceling, setCanceling] = useState(false);
  const [reinvitingDeclined, setReinvitingDeclined] = useState(false);
  const [invitingCanceled, setInvitingCanceled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestingPayment, setRequestingPayment] = useState(false);
  const [editingParticipantEmail, setEditingParticipantEmail] = useState(false);
  const currentUser = useCurrentUser();
  const trainer_id: number = training.trainer.id;
  const userIsTrainer = trainer_id === currentUser.id;

  useEffect(loadParticipants, [training.id]);

  const paymentActionsDisabled =
    paymentDisabled(values, participants) || withinSevenDays(training);
  const managePaymentDisabled = paymentDisabled(values, participants);
  const reinviteDisabled = reinviteDeclinedDisabled(values, participants);
  const cancelParticipantDisabled = cancelDisabled(
    values,
    participants,
    training
  );
  const inviteCanceledDisabled = addCanceledDisabled(values, participants);
  const editEmailDisabled = editParticipantEmailDisabled(
    values,
    participants,
    training
  );
  const exportParticipantsDisabled = exportDisabled(values);

  function loadParticipants() {
    setLoading(true);
    getTrainingParticipants(training.id).then(({ data }) => {
      setLoading(false);
      setParticipants(data.training_participants);
    });
  }

  function handleChange(newValues: number[]) {
    setValues(newValues);
  }

  function handlePayment() {
    const checkoutURL = queryString.stringifyUrl({
      url: RouteHelpers.getPath('td-trainings-view-card-checkout', {
        id: training.id
      }),
      query: { participants: values }
    });

    navigate(checkoutURL);
  }

  function handleManagePayment() {
    navigate(
      RouteHelpers.getPath('td-trainings-view-checkout', { id: training.id }),
      {
        state: { participants: values }
      }
    );
  }

  function handleCancel() {
    setCanceling(true);
  }

  function handleAfterCancel() {
    setCanceling(false);
    loadParticipants();
  }

  function handleInviteCanceled() {
    setInvitingCanceled(true);
  }

  function handleAfterReinviteDeclined() {
    setReinvitingDeclined(false);
    loadParticipants();
  }

  function handleAfterInviteCanceled() {
    setInvitingCanceled(false);
    loadParticipants();
  }

  function handleExport() {
    const url = exportUrl(values, training);
    window.open(url);
  }

  function handleRequestPayment() {
    setRequestingPayment(true);
  }

  function handleReinviteDeclined() {
    setReinvitingDeclined(true);
  }

  function handleAfterEditParticipantEmail() {
    setEditingParticipantEmail(false);
    loadParticipants();
  }

  function addParticipantsForm() {
    const isTrainingReopened = training.reopened_at !== null;

    if (isTrainingReopened) {
      return (
        <Permission
          zone="trainer_dashboard"
          resource="training_participants"
          or={false}
        >
          <AddParticipantForm
            trainingId={training.id}
            afterSubmit={loadParticipants}
          />
        </Permission>
      );
    }

    return (
      <AddParticipantForm
        trainingId={training.id}
        afterSubmit={loadParticipants}
      />
    );
  }

  return (
    <div className="p-4 mb-4 rounded bg-white shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
      <CancelModal
        open={canceling}
        participantIds={values}
        trainingId={training.id}
        afterCancel={handleAfterCancel}
        onClose={() => setCanceling(false)}
      />

      <ReinviteDeclinedModal
        open={reinvitingDeclined}
        participantIds={values}
        trainingId={training.id}
        afterReinviteDeclined={handleAfterReinviteDeclined}
        onClose={() => setReinvitingDeclined(false)}
      />

      <InviteCanceledModal
        open={invitingCanceled}
        participantIds={values}
        trainingId={training.id}
        afterCancel={handleAfterInviteCanceled}
        onClose={() => setInvitingCanceled(false)}
      />

      <RequestPaymentModal
        open={requestingPayment}
        participantIds={values}
        trainingId={training.id}
        onSuccess={loadParticipants}
        onClose={() => setRequestingPayment(false)}
      />

      <EditEmailModal
        open={editingParticipantEmail}
        trainingId={training.id}
        participantId={values[0]}
        participants={participants}
        onClose={() => setEditingParticipantEmail(false)}
        afterEdit={handleAfterEditParticipantEmail}
      />

      <div className="flex items-center justify-between mb-3 max-[440px]:flex-col max-[440px]:items-start">
        <Text as="h2" className="text-lg font-bold max-[440px]:mb-1">
          {i18n.ft(messages.participants)}
        </Text>

        <Permission
          zone="trainer_dashboard"
          resource="training_participants"
          or={userIsTrainer}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" shape="rounded" endIcon={ChevronDown}>
                {i18n.ft(messages.wantTo)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {trainingIsActive && (
                <>
                  <DropdownMenuItem
                    disabled={paymentActionsDisabled}
                    className={clsx(
                      paymentActionsDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={handlePayment}
                  >
                    <i className="fa-solid fa-credit-card" />
                    {i18n.ft(messages.payForParticipants)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={paymentActionsDisabled}
                    className={clsx(
                      paymentActionsDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={handleRequestPayment}
                  >
                    <i className="fa-solid fa-envelope" />
                    {i18n.ft(messages.requestPayment)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={reinviteDisabled}
                    className={clsx(
                      reinviteDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={handleReinviteDeclined}
                  >
                    <i className="fa-solid fa-user-plus" />
                    {i18n.ft(messages.reinviteDeclinedParticipants)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={editEmailDisabled}
                    className={clsx(
                      editEmailDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={() => setEditingParticipantEmail(true)}
                  >
                    <PencilEdit size={14} />
                    {i18n.ft(messages.editParticipantEmail)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={cancelParticipantDisabled}
                    className={clsx(
                      cancelParticipantDisabled &&
                        'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={handleCancel}
                  >
                    <i className="fa-solid fa-user-xmark" />
                    {i18n.ft(messages.cancelParticipants)}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={inviteCanceledDisabled}
                    className={clsx(
                      inviteCanceledDisabled && 'opacity-50 cursor-not-allowed'
                    )}
                    onSelect={handleInviteCanceled}
                  >
                    <i className="fa-solid fa-user-plus" />
                    {i18n.ft(messages.inviteCanceledParticipants)}
                  </DropdownMenuItem>

                  <Permission zone="trainer_dashboard" resource="trainings">
                    <DropdownMenuItem
                      disabled={managePaymentDisabled}
                      className={clsx(
                        managePaymentDisabled && 'opacity-50 cursor-not-allowed'
                      )}
                      onSelect={handleManagePayment}
                    >
                      <i className="fa-solid fa-money-bill" />
                      {i18n.ft(messages.managePayment)}
                    </DropdownMenuItem>
                  </Permission>
                </>
              )}

              <DropdownMenuItem
                disabled={exportParticipantsDisabled}
                className={clsx(
                  exportParticipantsDisabled && 'opacity-50 cursor-not-allowed'
                )}
                onSelect={handleExport}
              >
                <Download size={14} />
                {i18n.ft(messages.exportParticipants)}
              </DropdownMenuItem>

              <DropdownMenuArrow className="fill-ds-surface stroke-ds-border" />
            </DropdownMenuContent>
          </DropdownMenu>
        </Permission>
      </div>

      {trainingIsActive && addParticipantsForm()}

      {trainingIsActive && (
        <StatusBar
          total={getMaxParticipants(training)}
          invited={getTotalByStatus('invited', participants)}
          registered={getTotalByStatus('registered', participants)}
          pending={getTotalByStatus('pending', participants)}
        />
      )}

      <ParticipantList
        values={values}
        loading={loading}
        onChange={handleChange}
        options={mapParticipantsToOptions(participants)}
      />
    </div>
  );
}

export default Participants;
